import { defineComponent, PropType, reactive, set, watch } from '@vue/composition-api';
import { ConnectionStatus } from "@/views/connections/connections-types";
import { RecmanApiServiceInfo, RecmanApiServiceMutationResult, enableRecmanApi, updateRecmanApi, disableRecmanApi } from '@/functions/services-functions';
import { alertDialogue, confirmDialogue, generalError, inputDialogue } from '@/functions/system-modal-functions';
import { i18n } from "@/utilities/vue-i18n";
import { ModalIconType } from '../system-modals/system-modals';

type SettingsType = {
  connection: RecmanApiServiceInfo;
  loading: boolean;
  company: number;
};

const isMutationError = (service: RecmanApiServiceMutationResult): service is { error: 'api-key-conflict' } =>
  'error' in service && !!service.error;

export default defineComponent({
  props: {
    connection: {
      required: true,
      type: Object as PropType<RecmanApiServiceInfo>,
    },
    companyId: {
      required: false,
      type: Number,
    },
    allowDelete: {
      required: false,
      type: Boolean,
      default: false,
    },
    isAdmin: {
      required: false,
      type: Boolean,
      default: false,
    },
    loading: {
      required: false,
      type: Boolean,
      default: false,
    }
  },
  setup(props, context) {
    const settings = reactive<SettingsType>({
      connection: { ...props.connection },
      loading: props.loading,
      company: props.companyId ?? +context.root.$route.params.company,
    });

    watch(() => props.connection, (value) => settings.connection = { ...value }, { deep: true });
    watch(() => props.companyId, (value) => settings.company = value ?? +context.root.$route.params.company);
    watch(() => props.loading, (value) => settings.loading = value ?? settings.loading);

    const promptApiKey = async () => {
      return await inputDialogue({
        title: 'Recman API',
        message: i18n.t('components.recman-api-connection.enter-api-key'),
        validation: (i) => i?.trim().length > 0,
      });
    };

    const enable = async () => {
      if (settings.loading) {
        return;
      }

      settings.loading = true;

      const dialogue = await promptApiKey();

      if (dialogue.confirmed) {
        const service = await enableRecmanApi(settings.company, dialogue.input);

        if (service) {
          if (isMutationError(service)) {
            if (service.error === 'api-key-conflict') {
              await alertDialogue('Recman API', i18n.t('components.recman-api-connection.error-api-key-conflict') as string);
            }
          } else {
            settings.connection = service;
            context.emit('updated', service);
          }
        } else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    const update = async () => {
      if (settings.loading) {
        return;
      }

      settings.loading = true;

      const dialogue = await promptApiKey();

      if (dialogue.confirmed) {
        const service = await updateRecmanApi(settings.company, dialogue.input);

        if (service) {
          if (isMutationError(service)) {
            if (service.error === 'api-key-conflict') {
              await alertDialogue('Recman API', i18n.t('components.recman-api-connection.error-api-key-conflict') as string);
            }
          } else {
            set(settings.connection, 'id', service.id);
            set(settings.connection, 'status', service.status);
            set(settings.connection, 'active', service.active);
            set(settings.connection, 'apiKey', service.apiKey);
            set(settings.connection, 'defaultPromotionType', service.defaultPromotionType);
            context.emit('updated', service);
          }
        } else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    const disable = async () => {
      if (!props.allowDelete || settings.loading) {
        return;
      }

      settings.loading = true;

      const confirmed = await confirmDialogue(
        'Recman API',
        i18n.t('components.recman-api-connection.disable-integration'),
        ModalIconType.warning
      );

      if (confirmed) {
        if (await disableRecmanApi(settings.company)) {
          context.emit('updated', null);
        } else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    return {
      settings,
      ConnectionStatus,
      enable,
      update,
      disable,
    }
  },
})
